import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { X, Check, Scissors, Eraser, RotateCcw, ZoomIn, Sparkles, Eye, Image as ImageIcon } from 'lucide-react';

const SignatureEditor = ({ image, onSave, onCancel }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [sensitivity, setSensitivity] = useState(75); // 0-100 sensitivity, 75 is aggressive default
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const createImage = (url) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous'; // Avoid CORS issues
            img.onload = () => resolve(img);
            img.onerror = (error) => reject(error);
            img.src = url;
        });

    const processImage = async (isFinal = true) => {
        if (!croppedAreaPixels) return null;
        if (!isFinal) setProcessing(true);

        try {
            const img = await createImage(image);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const { x, y, width, height } = croppedAreaPixels;
            canvas.width = width;
            canvas.height = height;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(img, x, y, width, height, 0, 0, width, height);

            // V7 MANDATORY CLEANING: Logic always runs
            const imageData = ctx.getImageData(0, 0, width, height);
            const data = imageData.data;
            
            // Map sensitivity to threshold (0% -> 255, 100% -> 40)
            const threshold = 255 - (sensitivity * 2.15); 

            for (let i = 0; i < data.length; i += 4) {
                const r = data[i], g = data[i + 1], b = data[i + 2];
                const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

                if (luminance > threshold) {
                    data[i + 3] = 0; // Transparent
                } else {
                    // INK DARKENING
                    const darkening = 40 + (sensitivity * 0.5);
                    data[i] = Math.max(0, r - darkening);
                    data[i+1] = Math.max(0, g - darkening);
                    data[i+2] = Math.max(0, b - darkening);
                }
            }
            ctx.putImageData(imageData, 0, 0);
            return canvas.toDataURL('image/png');
        } catch (e) {
            console.error('Processing error:', e);
            return null;
        } finally {
            if (!isFinal) setProcessing(false);
        }
    };

    const handlePreview = async () => {
        const result = await processImage(false);
        if (result) {
            setPreviewUrl(result);
            setShowPreview(true);
        }
    };

    const handleSave = async () => {
        setProcessing(true);
        const result = await processImage(true);
        if (result) {
            onSave(result);
        } else {
            alert('Fehler bei der Bildverarbeitung.');
            setProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-gray-900/80 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="w-full max-w-xl bg-white dark:bg-[#1e1e1e] rounded-[3.5rem] overflow-hidden shadow-[0_40px_150px_rgba(0,0,0,0.7)] flex flex-col border border-white/5 animate-in zoom-in-95 duration-500">
                
                {/* Visual Header */}
                <div className="px-12 py-8 flex items-center justify-between border-b border-gray-100 dark:border-white/5 bg-gray-50/80 dark:bg-white/5">
                    <div className="flex items-center gap-5">
                        <div className="w-14 h-14 bg-plantlix-500 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-plantlix-600/20">
                            <Sparkles size={28} />
                        </div>
                        <div>
                            <h2 className="text-gray-900 dark:text-white font-black text-2xl tracking-tighter leading-none mb-1">Studio V7: Final Clean</h2>
                            <p className="text-[11px] text-plantlix-500 font-black uppercase tracking-[0.4em] opacity-80 italic">Auto-Transparency Enabled</p>
                        </div>
                    </div>
                    <button 
                        onClick={onCancel} 
                        className="p-4 hover:bg-gray-200 dark:hover:bg-red-500/10 text-gray-400 rounded-2xl transition-all"
                    >
                        <X size={28} />
                    </button>
                </div>

                {/* Cropper or Preview Area */}
                <div className="relative h-[350px] bg-black group">
                    {!showPreview ? (
                        <>
                            <Cropper
                                image={image}
                                crop={crop}
                                zoom={zoom}
                                aspect={2 / 1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                onMediaLoaded={() => setImageLoaded(true)}
                                style={{
                                    containerStyle: { background: '#000' }
                                }}
                            />
                            {imageLoaded && (
                                <div className="absolute top-6 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 backdrop-blur-md rounded-full text-[10px] text-white/60 font-black uppercase tracking-[0.2em] border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
                                    Wähle den Ausschnitt
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center p-8 bg-[#f8f8f8] dark:bg-[#0a0a0a] pattern-checkered">
                            <img src={previewUrl} alt="Preview" className="max-h-full max-w-full object-contain shadow-2xl rounded-lg" />
                            <div className="absolute top-6 left-1/2 -translate-x-1/2 px-6 py-2 bg-plantlix-500 text-white rounded-full text-xs font-black uppercase tracking-widest shadow-lg">
                                Vorschau: Hintergrund entfernt
                            </div>
                        </div>
                    )}
                    
                    {!imageLoaded && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black z-10">
                            <div className="w-10 h-10 border-4 border-plantlix-500 border-t-white rounded-full animate-spin"></div>
                            <span className="text-xs text-plantlix-500 font-black uppercase tracking-widest">Optimizing...</span>
                        </div>
                    )}
                </div>

                {/* Controls Area */}
                <div className="p-12 space-y-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {/* Zoom Control */}
                        <div className="space-y-5">
                            <div className="flex justify-between items-center px-1">
                                <div className="flex items-center gap-2">
                                    <ZoomIn size={16} className="text-gray-400" />
                                    <span className="text-xs font-black text-gray-500 uppercase tracking-widest">Ausschnitt</span>
                                </div>
                                <span className="text-sm font-black text-plantlix-500">{Math.round(zoom * 100)}%</span>
                            </div>
                            <input
                                type="range"
                                value={zoom}
                                min={1}
                                max={3}
                                step={0.01}
                                onChange={(e) => setZoom(parseFloat(e.target.value))}
                                className="w-full h-2.5 bg-gray-100 dark:bg-white/10 rounded-full appearance-none cursor-pointer accent-plantlix-500"
                            />
                        </div>

                        {/* Power Control */}
                        <div className="space-y-5">
                            <div className="flex justify-between items-center px-1">
                                <div className="flex items-center gap-2">
                                    <Eraser size={16} className="text-plantlix-500" />
                                    <span className="text-xs font-black text-gray-500 uppercase tracking-widest">Reinigung (Power)</span>
                                </div>
                                <span className="text-sm font-black text-plantlix-500">{sensitivity}%</span>
                            </div>
                            <input
                                type="range"
                                value={sensitivity}
                                min={0}
                                max={100}
                                step={1}
                                onChange={(e) => {
                                    setSensitivity(parseInt(e.target.value));
                                    if (showPreview) setShowPreview(false); // Hide outdated preview
                                }}
                                className="w-full h-2.5 bg-gray-100 dark:bg-white/10 rounded-full appearance-none cursor-pointer accent-plantlix-500"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-8 border-t border-gray-100 dark:border-white/5">
                        <button
                            onClick={() => {
                                setZoom(1); 
                                setCrop({x:0, y:0}); 
                                setSensitivity(75);
                                setShowPreview(false);
                            }}
                            className="flex items-center gap-3 text-xs font-black text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors uppercase tracking-[0.2em]"
                        >
                            <RotateCcw size={18} />
                            <span>Reset</span>
                        </button>

                        <div className="flex items-center gap-6">
                            <button
                                onClick={showPreview ? () => setShowPreview(false) : handlePreview}
                                disabled={processing || !imageLoaded}
                                className={`flex items-center gap-3 px-8 py-5 rounded-[1.8rem] font-black uppercase tracking-widest text-xs transition-all border-2 ${
                                    showPreview 
                                    ? 'bg-gray-900 border-gray-900 text-white' 
                                    : 'bg-transparent border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-plantlix-500 hover:text-plantlix-500'
                                }`}
                            >
                                {showPreview ? <ImageIcon size={20} /> : <Eye size={20} />}
                                <span>{showPreview ? 'Zurück zum Ausschnitt' : 'Vorschau'}</span>
                            </button>
                            
                            <button
                                onClick={handleSave}
                                disabled={processing || !imageLoaded}
                                className="bg-plantlix-500 hover:bg-plantlix-600 disabled:opacity-50 text-white px-12 py-6 rounded-[1.8rem] font-black shadow-[0_30px_60px_rgba(34,139,34,0.4)] transition-all active:scale-95 flex items-center gap-4 group"
                            >
                                {processing ? (
                                    <div className="w-6 h-6 border-3 border-white/20 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <Check size={28} strokeWidth={3} className="group-hover:scale-110 transition-transform" />
                                )}
                                <span className="tracking-tighter text-2xl">Fertig</span>
                            </button>
                        </div>
                    </div>
                </div>

            </div>
            <style>{`
                .pattern-checkered {
                    background-image: linear-gradient(45deg, #eee 25%, transparent 25%), 
                                      linear-gradient(-45deg, #eee 25%, transparent 25%), 
                                      linear-gradient(45deg, transparent 75%, #eee 75%), 
                                      linear-gradient(-45deg, transparent 75%, #eee 75%);
                    background-size: 20px 20px;
                    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
                }
                .dark .pattern-checkered {
                    background-image: linear-gradient(45deg, #1a1a1a 25%, transparent 25%), 
                                      linear-gradient(-45deg, #1a1a1a 25%, transparent 25%), 
                                      linear-gradient(45deg, transparent 75%, #1a1a1a 75%), 
                                      linear-gradient(-45deg, transparent 75%, #1a1a1a 75%);
                }
            `}</style>
        </div>
    );
};

export default SignatureEditor;
